/**
 * RGM Coin Rush — Phase 2 Admin Control Center
 * ============================================
 *
 * Admin UI for the Phase 1 Coin Rush backend (HEAD 496048a). This page
 * is READ-MOSTLY: all writes go through the already-approved Phase 1
 * routes and ONLY touch the `coin_rush_months` config collection. Nothing
 * on this page writes to gifts/wallets/earnings/payouts/Stripe.
 *
 * APIs consumed (Phase 1, unchanged):
 *   GET   /api/coin-rush/active
 *   GET   /api/coin-rush/leaderboard
 *   GET   /api/admin/coin-rush/months
 *   POST  /api/admin/coin-rush/months
 *   PATCH /api/admin/coin-rush/months/{key}
 *   POST  /api/admin/coin-rush/months/{key}/disqualify
 *   GET   /api/admin/coin-rush/breakdown/{uid}
 *
 * Reinstate (undo disqualification): intentionally NOT exposed. Phase 1
 * backend has no reinstate endpoint and the Admin Control Center brief
 * instructed us to report it as missing rather than widen backend scope.
 *
 * Phase 2 boundaries (strict):
 *   • No scoring changes.
 *   • No financial writes.
 *   • No public UI.
 *   • No deployment.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const fmtDtLocal = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

// Convert an ISO timestamp (UTC) into the value a <input type="datetime-local">
// expects (local-time YYYY-MM-DDTHH:MM). Empty if iso is missing.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
};

// Convert a <input type="datetime-local"> value (local) into a UTC ISO
// string the backend `_parse_dt` helper accepts.
const localInputToIso = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
};

const monthKeyFromStart = (local) => {
  if (!local) return "";
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const StatusPill = ({ active, enabled }) => {
  if (active) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-black border bg-emerald-500/15 border-emerald-400/50 text-emerald-100"
        data-testid="coinrush-status-pill"
      >
        <span className="w-2 h-2 rounded-full bg-emerald-400" /> ACTIVE
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-black border bg-white/5 border-white/15 text-gray-300"
      data-testid="coinrush-status-pill"
    >
      <span className="w-2 h-2 rounded-full bg-gray-500" />
      {enabled ? "SCHEDULED" : "INACTIVE"}
    </span>
  );
};

const ConfirmModal = ({ open, title, body, onCancel, onConfirm, confirmLabel = "Confirm", danger }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="coinrush-confirm-modal"
    >
      <div
        className="w-full max-w-md bg-[#13131a] border border-purple-500/40 rounded-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-black text-white">{title}</h3>
        <div className="text-sm text-gray-300 mt-2 space-y-2">{body}</div>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            className="text-white border-gray-700"
            data-testid="coinrush-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className={`${danger ? "bg-rose-600 hover:bg-rose-500" : "bg-purple-600 hover:bg-purple-500"} text-white font-black`}
            data-testid="coinrush-confirm-ok"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminCoinRushPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [bootError, setBootError] = useState(null);

  const [active, setActive] = useState(null);        // GET /coin-rush/active
  const [months, setMonths] = useState(null);        // GET /admin/coin-rush/months
  const [leaderboard, setLeaderboard] = useState(null); // GET /coin-rush/leaderboard
  const [lbTotal, setLbTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");

  // Form state for the upsert / patch controls.
  const [form, setForm] = useState({
    month_key: "",
    title: "",
    enabled: false,
    start_local: "",
    end_local: "",
  });
  const [saving, setSaving] = useState(false);

  // Participant inspection
  const [inspectedUid, setInspectedUid] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [inspectionLoading, setInspectionLoading] = useState(false);

  // Confirm modal
  const [confirm, setConfirm] = useState(null); // { title, body, onConfirm, danger, confirmLabel }

  // --- Boot: identity check (same pattern as other admin pages) ---
  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        const u = r.data;
        const isFounder = (u?.badges || []).includes("founder");
        const isAdmin = u?.role === "admin" || (u?.badges || []).includes("admin");
        if (!isFounder && !isAdmin) {
          setBootError("Founder/admin access only.");
          return;
        }
        setMe(u);
      })
      .catch(() => setBootError("You must be signed in as an admin."));
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m, lb] = await Promise.all([
        api.get("/coin-rush/active"),
        api.get("/admin/coin-rush/months", { params: { limit: 24 } }),
        api.get("/coin-rush/leaderboard", { params: { limit: 200 } }),
      ]);
      setActive(a.data || null);
      setMonths(Array.isArray(m.data?.months) ? m.data.months : []);
      setLeaderboard(Array.isArray(lb.data?.leaderboard) ? lb.data.leaderboard : []);
      setLbTotal(Number(lb.data?.total_participants || 0));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not load Coin Rush data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!me) return;
    refreshAll();
  }, [me, refreshAll]);

  // Load a month into the form for editing.
  const loadMonthIntoForm = useCallback((m) => {
    if (!m) {
      setForm({ month_key: "", title: "", enabled: false, start_local: "", end_local: "" });
      setSelectedKey("");
      return;
    }
    setForm({
      month_key: m.month_key || "",
      title: m.title || "",
      enabled: !!m.enabled,
      start_local: isoToLocalInput(m.start_at),
      end_local: isoToLocalInput(m.end_at),
    });
    setSelectedKey(m.month_key || "");
  }, []);

  // Default-select the active month (or the most recent) when data loads.
  useEffect(() => {
    if (!months) return;
    if (selectedKey) return;
    const activeKey = active?.active ? active?.month?.month_key : null;
    const target = (activeKey && months.find((x) => x.month_key === activeKey)) || months[0];
    if (target) loadMonthIntoForm(target);
  }, [months, active, selectedKey, loadMonthIntoForm]);

  const saveMonth = async () => {
    // Auto-fill month_key from start date if blank.
    const key = (form.month_key || monthKeyFromStart(form.start_local)).trim();
    if (!/^\d{4}-\d{2}$/.test(key)) {
      toast.error("month_key must be YYYY-MM (e.g. 2026-10)");
      return;
    }
    const startIso = localInputToIso(form.start_local);
    const endIso = localInputToIso(form.end_local);
    if (!startIso || !endIso) {
      toast.error("start and end are required");
      return;
    }
    if (new Date(endIso) <= new Date(startIso)) {
      toast.error("end must be after start");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/coin-rush/months", {
        month_key: key,
        title: form.title || `Coin Rush — ${key}`,
        enabled: !!form.enabled,
        start_at: startIso,
        end_at: endIso,
      });
      toast.success(`Saved Coin Rush ${key}`);
      await refreshAll();
      setSelectedKey(key);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (m) => {
    const next = !m.enabled;
    setConfirm({
      title: next ? "Enable Coin Rush?" : "Disable Coin Rush?",
      body: (
        <>
          <p>
            {next
              ? `This will mark "${m.title}" as enabled. It becomes ACTIVE only inside the configured start/end window.`
              : `This will mark "${m.title}" as disabled. Public endpoints will stop returning this as active immediately.`}
          </p>
          <p className="text-[11px] text-gray-500">
            No gifts / coins / earnings / payouts are touched by this change.
          </p>
        </>
      ),
      confirmLabel: next ? "Enable" : "Disable",
      danger: !next,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.patch(`/admin/coin-rush/months/${m.month_key}`, { enabled: next });
          toast.success(`${m.month_key}: ${next ? "enabled" : "disabled"}`);
          await refreshAll();
        } catch (e) {
          const d = e?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Toggle failed");
        }
      },
    });
  };

  const inspectParticipant = async (uid) => {
    setInspectedUid(uid);
    setInspection(null);
    setInspectionLoading(true);
    try {
      const r = await api.get(`/admin/coin-rush/breakdown/${encodeURIComponent(uid)}`);
      setInspection(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not load breakdown");
    } finally {
      setInspectionLoading(false);
    }
  };

  const disqualifyParticipant = (uid, label) => {
    const key = active?.month?.month_key;
    if (!key) {
      toast.error("No active month to disqualify against.");
      return;
    }
    setConfirm({
      title: "Disqualify from Coin Rush?",
      body: (
        <div className="space-y-2">
          <p>
            This will add <code className="text-purple-200">{uid}</code>
            {label ? ` (${label})` : ""} to the excluded list for{" "}
            <code className="text-purple-200">{key}</code>.
          </p>
          <ul className="list-disc list-inside text-[12px] text-gray-400 space-y-0.5">
            <li>Removes them from the Coin Rush ranking.</li>
            <li>Does NOT delete any gifts.</li>
            <li>Does NOT alter coins.</li>
            <li>Does NOT alter artist earnings.</li>
            <li>Does NOT alter payouts.</li>
          </ul>
          <p className="text-[11px] text-amber-200">
            Reinstate is not supported by the current backend. Removing an exclusion
            will require a future backend change.
          </p>
        </div>
      ),
      confirmLabel: "Disqualify",
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.post(`/admin/coin-rush/months/${key}/disqualify`, { user_id: uid });
          toast.success(`Disqualified ${uid}`);
          await refreshAll();
          if (inspectedUid === uid) await inspectParticipant(uid);
        } catch (e) {
          const d = e?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Disqualify failed");
        }
      },
    });
  };

  const activeMonth = active?.month || null;
  const activeKey = activeMonth?.month_key || null;
  const excludedForActive = useMemo(() => {
    if (!months || !activeKey) return [];
    const m = months.find((x) => x.month_key === activeKey);
    return (m && m.excluded_user_ids) || [];
  }, [months, activeKey]);

  if (bootError) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center p-6" data-testid="coinrush-blocked">
        <Card className="max-w-md p-6 bg-white/5 border-rose-500/30">
          <h1 className="text-xl font-black text-white">🛑 Admin Coin Rush</h1>
          <p className="text-rose-300 mt-2">{bootError}</p>
          <Button onClick={() => navigate("/")} className="mt-4 bg-purple-600 text-white">Home</Button>
        </Card>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center" data-testid="coinrush-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="admin-coin-rush-page">
      <SharedNavigation currentUser={me} activePage="admin" />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3">
              <span>🪙</span>
              <span>Coin Rush — Admin Control Center</span>
            </h1>
            <p className="text-sm text-gray-400 mt-1 max-w-3xl">
              Promotional, financially-inert leaderboard. Points are derived live from
              <code className="mx-1 text-purple-200">gift_transactions</code> and are
              <strong className="text-white"> never cashable</strong>. This page writes
              only to Coin Rush configuration — gifts, coins, earnings, and payouts are
              untouched.
            </p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <StatusPill active={!!active?.active} enabled={!!activeMonth?.enabled} />
            <Button
              onClick={refreshAll}
              variant="outline"
              className="text-white border-gray-700"
              disabled={loading}
              data-testid="coinrush-refresh"
            >
              {loading ? "Refreshing…" : "🔄 Refresh"}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/control-center")}
              className="text-white border-gray-700"
              data-testid="coinrush-back-to-cc"
            >
              ← Control Center
            </Button>
          </div>
        </div>

        {/* 1. Status + Game Controls */}
        <section className="mb-8" data-testid="coinrush-section-status">
          <h2 className="text-xl font-black text-white mb-3">1. Coin Rush Status</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card className="p-4 bg-white/[0.03] border-white/10">
              <p className="text-[11px] uppercase tracking-widest text-gray-400">Current State</p>
              <div className="mt-2 space-y-1 text-sm">
                <p className="text-white font-black text-base" data-testid="coinrush-active-title">
                  {activeMonth?.title || "No month configured"}
                </p>
                <p className="text-gray-300" data-testid="coinrush-active-key">
                  month_key:{" "}
                  <code className="text-purple-200">
                    {activeMonth?.month_key || "—"}
                  </code>
                </p>
                <p className="text-gray-300">
                  Starts: <span data-testid="coinrush-active-start">{fmtDtLocal(activeMonth?.start_at)}</span>
                </p>
                <p className="text-gray-300">
                  Ends: <span data-testid="coinrush-active-end">{fmtDtLocal(activeMonth?.end_at)}</span>
                </p>
                <p className="text-gray-300">
                  Enabled:{" "}
                  <strong
                    className={activeMonth?.enabled ? "text-emerald-300" : "text-rose-300"}
                    data-testid="coinrush-active-enabled"
                  >
                    {activeMonth?.enabled ? "yes" : "no"}
                  </strong>
                </p>
                <p className="text-gray-300">
                  Finalised:{" "}
                  <strong className="text-white" data-testid="coinrush-active-finalised">
                    {activeMonth?.finalised ? "yes" : "no"}
                  </strong>
                </p>
                <p className="text-[11px] text-gray-500 mt-2">
                  "Active" means enabled AND the current time falls between start_at and end_at.
                  Finalisation (month-close snapshot filtering refunds) is Phase 5 — not yet built.
                </p>
              </div>
            </Card>

            <Card className="p-4 bg-white/[0.03] border-white/10">
              <p className="text-[11px] uppercase tracking-widest text-gray-400">CEO Game Controls</p>
              <p className="text-[11px] text-gray-500 mt-1 mb-3">
                Scoring weights are CEO-locked for V1 — only title, enabled, start, and end are editable.
              </p>
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="text-xs text-gray-300 min-w-0">
                    month_key
                    <Input
                      value={form.month_key}
                      onChange={(e) => setForm((f) => ({ ...f, month_key: e.target.value }))}
                      placeholder="2026-10"
                      className="mt-1 w-full bg-black/40 border-white/10 text-white"
                      data-testid="coinrush-input-month-key"
                    />
                  </label>
                  <label className="text-xs text-gray-300 min-w-0">
                    title
                    <Input
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="Coin Rush — October 2026"
                      className="mt-1 w-full bg-black/40 border-white/10 text-white"
                      data-testid="coinrush-input-title"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="text-xs text-gray-300 min-w-0">
                    start_at (local time)
                    <input
                      type="datetime-local"
                      value={form.start_local}
                      onChange={(e) => setForm((f) => ({ ...f, start_local: e.target.value }))}
                      className="mt-1 block w-full min-w-0 max-w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm box-border"
                      data-testid="coinrush-input-start"
                    />
                  </label>
                  <label className="text-xs text-gray-300 min-w-0">
                    end_at (local time)
                    <input
                      type="datetime-local"
                      value={form.end_local}
                      onChange={(e) => setForm((f) => ({ ...f, end_local: e.target.value }))}
                      className="mt-1 block w-full min-w-0 max-w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm box-border"
                      data-testid="coinrush-input-end"
                    />
                  </label>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-200 mt-1">
                  <input
                    type="checkbox"
                    checked={!!form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                    data-testid="coinrush-input-enabled"
                  />
                  enabled
                </label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    onClick={saveMonth}
                    disabled={saving}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-black"
                    data-testid="coinrush-save"
                  >
                    {saving ? "Saving…" : "💾 Save month"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => loadMonthIntoForm(null)}
                    className="text-white border-gray-700"
                    data-testid="coinrush-reset-form"
                  >
                    Reset form
                  </Button>
                </div>
              </div>
            </Card>
          </div>

          {/* Configured months table */}
          <Card className="mt-3 p-3 bg-white/[0.03] border-white/10">
            <p className="text-white text-sm font-bold mb-2">All configured months</p>
            {!months ? (
              <p className="text-xs text-gray-500">Loading…</p>
            ) : months.length === 0 ? (
              <p className="text-xs text-gray-500" data-testid="coinrush-months-empty">
                No Coin Rush months configured yet. Use the form above to create one.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="coinrush-months-table">
                  <thead>
                    <tr className="text-left text-gray-500 uppercase tracking-widest">
                      <th className="py-2 pr-3">month_key</th>
                      <th className="py-2 pr-3">title</th>
                      <th className="py-2 pr-3">enabled</th>
                      <th className="py-2 pr-3">start_at</th>
                      <th className="py-2 pr-3">end_at</th>
                      <th className="py-2 pr-3">excluded</th>
                      <th className="py-2 pr-3 text-right">actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m) => (
                      <tr
                        key={m.month_key}
                        className={`border-t border-white/5 ${
                          m.month_key === activeKey ? "bg-emerald-500/5" : ""
                        }`}
                        data-testid={`coinrush-month-row-${m.month_key}`}
                      >
                        <td className="py-2 pr-3 font-mono text-white">{m.month_key}</td>
                        <td className="py-2 pr-3 text-gray-200">{m.title}</td>
                        <td className="py-2 pr-3">
                          <span className={m.enabled ? "text-emerald-300" : "text-gray-400"}>
                            {m.enabled ? "yes" : "no"}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-gray-300">{fmtDtLocal(m.start_at)}</td>
                        <td className="py-2 pr-3 text-gray-300">{fmtDtLocal(m.end_at)}</td>
                        <td className="py-2 pr-3 text-gray-300">
                          {(m.excluded_user_ids || []).length}
                        </td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => loadMonthIntoForm(m)}
                            className="text-purple-300 hover:text-purple-200 underline mr-3"
                            data-testid={`coinrush-month-edit-${m.month_key}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleEnabled(m)}
                            className={`${m.enabled ? "text-rose-300" : "text-emerald-300"} hover:underline`}
                            data-testid={`coinrush-month-toggle-${m.month_key}`}
                          >
                            {m.enabled ? "Disable" : "Enable"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        {/* 2. Leaderboard Preview */}
        <section className="mb-8" data-testid="coinrush-section-leaderboard">
          <div className="flex items-end justify-between gap-2 mb-3 flex-wrap">
            <h2 className="text-xl font-black text-white">2. Leaderboard Preview</h2>
            <span className="text-[11px] text-gray-400" data-testid="coinrush-lb-total">
              {lbTotal} participants
            </span>
          </div>
          {!active?.active ? (
            <Card className="p-4 bg-amber-500/5 border-amber-500/30 text-sm text-amber-200" data-testid="coinrush-lb-inactive">
              No month is currently ACTIVE. The leaderboard only renders when a month is enabled and inside its start/end window.
            </Card>
          ) : !leaderboard ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : leaderboard.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="coinrush-lb-empty">
              No qualifying paid gifts have been recorded during the active window yet.
            </p>
          ) : (
            <Card className="p-3 bg-white/[0.03] border-white/10 overflow-x-auto">
              <table className="w-full text-xs" data-testid="coinrush-leaderboard-table">
                <thead>
                  <tr className="text-left text-gray-500 uppercase tracking-widest">
                    <th className="py-2 pr-3">rank</th>
                    <th className="py-2 pr-3">supporter</th>
                    <th className="py-2 pr-3 text-right">points</th>
                    <th className="py-2 pr-3 text-right">gifts</th>
                    <th className="py-2 pr-3 text-right">unique artists</th>
                    <th className="py-2 pr-3 text-right">base</th>
                    <th className="py-2 pr-3 text-right">diversity</th>
                    <th className="py-2 pr-3 text-right">milestone</th>
                    <th className="py-2 pr-3 text-right">actions</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row) => (
                    <tr
                      key={row.sender_id}
                      className="border-t border-white/5"
                      data-testid={`coinrush-lb-row-${row.sender_id}`}
                    >
                      <td className="py-2 pr-3 text-white font-black">#{row.rank}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {row.profile_photo && (
                            <img
                              src={optimizeImage(row.profile_photo, 'avatar')}
                              alt=""
                              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          )}
                          <div className="min-w-0">
                            <p className="text-white truncate">{row.artist_name}</p>
                            <p className="text-[10px] text-gray-500 font-mono truncate">{row.sender_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right text-amber-200 font-black">{row.points}</td>
                      <td className="py-2 pr-3 text-right text-gray-200">{row.qualifying_gift_count}</td>
                      <td className="py-2 pr-3 text-right text-gray-200">{row.unique_artists_supported}</td>
                      <td className="py-2 pr-3 text-right text-gray-400">{row.base_gift_points}</td>
                      <td className="py-2 pr-3 text-right text-gray-400">{row.diversity_points}</td>
                      <td className="py-2 pr-3 text-right text-gray-400">{row.milestone_points}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => inspectParticipant(row.sender_id)}
                          className="text-purple-300 hover:text-purple-200 underline"
                          data-testid={`coinrush-lb-inspect-${row.sender_id}`}
                        >
                          Inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </section>

        {/* 3. Participant Inspection */}
        <section className="mb-8" data-testid="coinrush-section-inspection">
          <div className="flex items-end justify-between gap-2 mb-3 flex-wrap">
            <h2 className="text-xl font-black text-white">3. Participant Inspection</h2>
            <div className="flex gap-2 items-center">
              <Input
                placeholder="Enter user_id to inspect"
                value={inspectedUid || ""}
                onChange={(e) => setInspectedUid(e.target.value)}
                className="w-72 bg-black/40 border-white/10 text-white"
                data-testid="coinrush-inspect-input"
              />
              <Button
                onClick={() => inspectedUid && inspectParticipant(inspectedUid.trim())}
                disabled={!inspectedUid || inspectionLoading}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="coinrush-inspect-btn"
              >
                {inspectionLoading ? "Loading…" : "Inspect"}
              </Button>
            </div>
          </div>

          {!inspection ? (
            <p className="text-sm text-gray-500">
              {inspectionLoading
                ? "Loading breakdown…"
                : "Select a row above or paste a user_id to see the full score derivation."}
            </p>
          ) : (
            <Card className="p-4 bg-white/[0.03] border-white/10 space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs text-gray-400">
                    month: <code className="text-purple-200">{inspection.month?.month_key}</code>
                  </p>
                  <p className="text-sm text-white font-bold mt-1" data-testid="coinrush-inspection-uid">
                    sender_id: <code className="text-purple-200">{inspection.sender_id}</code>
                    {inspection.disqualified && (
                      <span
                        className="ml-2 text-[10px] font-black px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/50 text-rose-100"
                        data-testid="coinrush-inspection-disqualified"
                      >
                        DISQUALIFIED
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => disqualifyParticipant(inspection.sender_id, "")}
                  disabled={inspection.disqualified || !active?.active}
                  className="bg-rose-600 hover:bg-rose-500 text-white font-black"
                  data-testid="coinrush-disqualify-btn"
                >
                  {inspection.disqualified ? "Already excluded" : "Disqualify"}
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded bg-black/40 border border-white/10 p-2">
                  <p className="uppercase text-[10px] text-gray-500">Points</p>
                  <p className="text-amber-200 text-lg font-black" data-testid="coinrush-inspection-points">
                    {inspection.score?.points ?? 0}
                  </p>
                </div>
                <div className="rounded bg-black/40 border border-white/10 p-2">
                  <p className="uppercase text-[10px] text-gray-500">Qualifying gifts</p>
                  <p className="text-white text-lg font-black">
                    {inspection.score?.qualifying_gift_count ?? 0}
                  </p>
                </div>
                <div className="rounded bg-black/40 border border-white/10 p-2">
                  <p className="uppercase text-[10px] text-gray-500">Unique artists</p>
                  <p className="text-white text-lg font-black">
                    {inspection.score?.unique_artists_supported ?? 0}
                  </p>
                </div>
                <div className="rounded bg-black/40 border border-white/10 p-2">
                  <p className="uppercase text-[10px] text-gray-500">Breakdown</p>
                  <p className="text-gray-300 text-[11px]">
                    base {inspection.score?.base_gift_points ?? 0} ·
                    div {inspection.score?.diversity_points ?? 0} ·
                    ms {inspection.score?.milestone_points ?? 0}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="coinrush-inspection-table">
                  <thead>
                    <tr className="text-left text-gray-500 uppercase tracking-widest">
                      <th className="py-2 pr-3">#</th>
                      <th className="py-2 pr-3">created_at</th>
                      <th className="py-2 pr-3">recipient_id</th>
                      <th className="py-2 pr-3 text-right">coins</th>
                      <th className="py-2 pr-3">source</th>
                      <th className="py-2 pr-3 text-right">base</th>
                      <th className="py-2 pr-3 text-right">diversity</th>
                      <th className="py-2 pr-3">tx_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inspection.qualifying_gifts || []).map((g, i) => {
                      const brk = g.coin_source_breakdown || {};
                      const paid = Number(brk.paid || 0);
                      const legacyFallback = !(paid > 0) && g.coin_source === "paid";
                      return (
                        <tr
                          key={g.transaction_id || i}
                          className="border-t border-white/5"
                          data-testid={`coinrush-gift-row-${i}`}
                        >
                          <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                          <td className="py-2 pr-3 text-gray-300">{fmtDtLocal(g.created_at)}</td>
                          <td className="py-2 pr-3 font-mono text-white break-all">
                            {g.recipient_id || "—"}
                          </td>
                          <td className="py-2 pr-3 text-right text-gray-200">{g.coins ?? "—"}</td>
                          <td className="py-2 pr-3">
                            {legacyFallback ? (
                              <span
                                className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200"
                                data-testid={`coinrush-gift-legacy-${i}`}
                                title="Qualified using legacy coin_source == 'paid' fallback — exact coin_source_breakdown was unavailable."
                              >
                                legacy paid-source
                              </span>
                            ) : (
                              <code className="text-[10px] text-gray-300">
                                paid:{paid} · reward:{brk.reward ?? 0} · bonus:{brk.bonus ?? 0}
                              </code>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right text-emerald-300">+{g.awarded_base}</td>
                          <td className="py-2 pr-3 text-right text-fuchsia-300">
                            {g.awarded_diversity ? `+${g.awarded_diversity}` : "—"}
                          </td>
                          <td className="py-2 pr-3 font-mono text-[10px] text-gray-500 break-all">
                            {g.transaction_id || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(inspection.qualifying_gifts || []).length === 0 && (
                <p className="text-xs text-gray-500" data-testid="coinrush-inspection-empty">
                  No qualifying gifts in this window for this user.
                </p>
              )}
            </Card>
          )}
        </section>

        {/* 4. Current month exclusions */}
        {active?.active && (
          <section className="mb-8" data-testid="coinrush-section-exclusions">
            <h2 className="text-xl font-black text-white mb-3">
              4. Excluded participants · {activeKey}
            </h2>
            <Card className="p-4 bg-white/[0.03] border-white/10">
              {excludedForActive.length === 0 ? (
                <p className="text-xs text-gray-500" data-testid="coinrush-excluded-empty">
                  Nobody is currently excluded from this month&apos;s Coin Rush.
                </p>
              ) : (
                <ul className="space-y-1 text-xs" data-testid="coinrush-excluded-list">
                  {excludedForActive.map((uid) => (
                    <li
                      key={uid}
                      className="flex items-center justify-between gap-3"
                      data-testid={`coinrush-excluded-${uid}`}
                    >
                      <code className="text-rose-200 font-mono break-all">{uid}</code>
                      <button
                        type="button"
                        onClick={() => inspectParticipant(uid)}
                        className="text-purple-300 hover:text-purple-200 underline flex-shrink-0"
                        data-testid={`coinrush-excluded-inspect-${uid}`}
                      >
                        Inspect
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-amber-200 mt-3">
                Reinstating (removing from this list) is not supported by the current Phase 1
                backend. A future phase could add
                <code className="mx-1 text-amber-100">POST /api/admin/coin-rush/months/{"{key}"}/reinstate</code>
                — flagged for CEO approval before being built.
              </p>
            </Card>
          </section>
        )}
      </div>

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        onCancel={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
      />
    </div>
  );
};

export default AdminCoinRushPage;
